import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStudentAssessmentsQuery } from "@/hooks/useStudents";
import { startOrResumeAssessment } from "@/services/repositories/assessment-repository";
import type { Assessment, AssessmentEntryMode } from "@/types/assessment";
import { formatDate } from "@/utils/date";
import {
  assessmentEntryMode,
  assessmentRecordLabel,
  entryModeLabel,
  studentWorkspacePaths,
} from "@/utils/student-workspace";

const ASSESSMENT_OPTIONS = [
  {
    type: "scale" as const,
    title: "完整量表评估",
    description: "按领域和条目逐项评分，适合系统完成整套评估。",
  },
  {
    type: "activity" as const,
    title: "活动评估",
    description: "在活动场景中观察儿童表现，并同步完成关联条目。",
  },
];

export function AssessmentsLandingPage(): JSX.Element {
  const { studentId = "" } = useParams<{ studentId: string }>();
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);
  const navigate = useNavigate();
  const [startingType, setStartingType] = useState<AssessmentEntryMode>();
  const [startError, setStartError] = useState<string>();

  const selectedAssessments = useMemo(
    () =>
      [...(assessmentsQuery.data ?? [])].sort(
        (left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime(),
      ),
    [assessmentsQuery.data],
  );
  const activeAssessment = selectedAssessments.find(
    (assessment) => assessment.status === "in_progress",
  );

  const handleStart = async (type: AssessmentEntryMode) => {
    if (!studentId || startingType) return;
    setStartingType(type);
    setStartError(undefined);
    try {
      const assessment = await startOrResumeAssessment(studentId, type);
      await assessmentsQuery.refetch();
      navigate(studentWorkspacePaths.assessment(studentId, type, assessment.id));
    } catch (error) {
      setStartError(error instanceof Error ? error.message : "无法开始评估，请稍后重试");
    } finally {
      setStartingType(undefined);
    }
  };

  const handleContinue = () => {
    if (!activeAssessment) return;
    const mode = assessmentEntryMode(activeAssessment);
    navigate(studentWorkspacePaths.assessment(studentId, mode, activeAssessment.id));
  };

  return (
    <div className="space-y-6">
      {assessmentsQuery.isLoading ? (
        <p className="text-sm text-slate-500" role="status">
          正在加载评估记录…
        </p>
      ) : null}
      {startError ? (
        <p
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {startError}
        </p>
      ) : null}

      {activeAssessment ? (
        <section aria-labelledby="in-progress-assessments">
          <h2 id="in-progress-assessments" className="text-xl font-semibold text-slate-800">
            继续评估
          </h2>
          <div className="mt-4 grid gap-5 md:grid-cols-2">
            <InProgressAssessmentCard
              assessment={activeAssessment}
              title={entryModeLabel(assessmentEntryMode(activeAssessment))}
              disabled={Boolean(startingType)}
              onContinue={handleContinue}
            />
          </div>
        </section>
      ) : null}

      {!activeAssessment ? (
        <section aria-labelledby="new-assessment">
          <h2 id="new-assessment" className="text-xl font-semibold text-slate-800">
            选择评估方式
          </h2>
          <div className="mt-4 grid gap-5 md:grid-cols-2">
            {ASSESSMENT_OPTIONS.map((option) => (
              <AssessmentCard
                key={option.type}
                type={option.type}
                title={option.title}
                description={option.description}
                actionLabel={`开始${option.title}`}
                disabled={!studentId || Boolean(startingType)}
                pending={startingType === option.type}
                onStart={() => void handleStart(option.type)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {!assessmentsQuery.isLoading && !assessmentsQuery.isError ? (
        <section
          aria-labelledby="assessment-records"
          className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-[0_18px_45px_-36px_rgba(51,65,85,0.5)] md:p-6"
        >
          <div className="flex items-center justify-between gap-4">
            <h2 id="assessment-records" className="text-lg font-semibold text-slate-800">
              评估记录
            </h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
              共 {selectedAssessments.length} 次
            </span>
          </div>
          {selectedAssessments.length > 0 ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {selectedAssessments.map((assessment) => {
                const completed = assessment.status === "completed";
                const target = completed
                  ? studentWorkspacePaths.reports(studentId, assessment.id)
                  : studentWorkspacePaths.assessment(
                      studentId,
                      assessmentEntryMode(assessment),
                      assessment.id,
                    );
                return (
                  <li
                    key={assessment.id}
                    className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          {assessmentRecordLabel()}
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            completed ? "bg-brand/10 text-brand-dark" : "bg-sky-100 text-sky-800"
                          }`}
                        >
                          {completed ? "已完成" : "进行中"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {completed ? "完成于" : "开始于"}{" "}
                        {formatDate(assessment.submittedAt ?? assessment.startedAt)}
                      </p>
                    </div>
                    <Link
                      to={target}
                      className="inline-flex min-h-11 shrink-0 items-center justify-center self-start rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-brand hover:text-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:self-auto"
                    >
                      {completed ? "查看报告" : "继续评估"}
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-slate-500">暂无评估记录。</p>
          )}
        </section>
      ) : null}
    </div>
  );
}

function InProgressAssessmentCard({
  assessment,
  title,
  disabled,
  onContinue,
}: {
  assessment: Assessment;
  title: string;
  disabled: boolean;
  onContinue: () => void;
}): JSX.Element {
  return (
    <article className="relative isolate flex min-h-56 overflow-hidden rounded-[1.75rem] border border-brand/25 bg-gradient-to-br from-brand/[0.13] via-white to-white p-6 shadow-[0_24px_55px_-34px_rgba(63,101,29,0.72)] md:p-7">
      <span
        className="pointer-events-none absolute -right-8 -top-12 -z-10 h-44 w-44 rounded-full bg-brand/10"
        aria-hidden="true"
      />
      <div className="flex w-full flex-col justify-between gap-8 sm:flex-row sm:items-end">
        <div>
          <span className="inline-flex rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-brand-dark shadow-sm">
            进行中
          </span>
          <h3 className="mt-4 text-2xl font-semibold tracking-tight text-slate-800">{title}</h3>
          <p className="mt-2 text-sm text-slate-500">开始于 {formatDate(assessment.startedAt)}</p>
        </div>
        <button
          type="button"
          onClick={onContinue}
          disabled={disabled}
          className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_-14px_rgba(63,101,29,0.9)] transition hover:-translate-y-0.5 hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {`继续${title}`}
          <span className="ml-2" aria-hidden="true">
            →
          </span>
        </button>
      </div>
    </article>
  );
}

function AssessmentCard({
  type,
  title,
  description,
  actionLabel,
  disabled,
  pending,
  onStart,
}: {
  type: AssessmentEntryMode;
  title: string;
  description: string;
  actionLabel: string;
  disabled: boolean;
  pending: boolean;
  onStart: () => void;
}): JSX.Element {
  const isScale = type === "scale";
  return (
    <article
      className={`relative isolate flex min-h-[17rem] flex-col justify-between overflow-hidden rounded-[1.75rem] border p-7 shadow-[0_28px_60px_-40px_rgba(31,50,18,0.75)] transition hover:-translate-y-1 hover:shadow-[0_32px_65px_-38px_rgba(31,50,18,0.72)] ${
        isScale
          ? "border-orange-200/80 bg-gradient-to-br from-white via-white to-accent-warm-soft"
          : "border-teal-200/80 bg-gradient-to-br from-white via-white to-accent-fresh-soft"
      }`}
    >
      <span
        className={`pointer-events-none absolute -right-8 -top-10 -z-10 flex h-44 w-44 items-end justify-start rounded-full pb-9 pl-8 text-5xl font-semibold tracking-tighter ${
          isScale ? "bg-orange-100/75 text-accent-warm/20" : "bg-teal-100/75 text-accent-fresh/20"
        }`}
        aria-hidden="true"
      >
        {isScale ? "01" : "02"}
      </span>
      <div>
        <p
          className={`text-xs font-semibold uppercase tracking-[0.18em] ${isScale ? "text-accent-warm-dark" : "text-accent-fresh"}`}
        >
          {isScale ? "按条目逐项评分" : "在活动中自然观察"}
        </p>
        <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-800">{title}</h2>
        <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">{description}</p>
      </div>
      <button
        type="button"
        onClick={onStart}
        disabled={disabled}
        className="mt-8 inline-flex min-h-12 w-fit items-center justify-center rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_-14px_rgba(63,101,29,0.9)] transition hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {pending ? "正在准备…" : actionLabel}
        {!pending ? (
          <span className="ml-2" aria-hidden="true">
            →
          </span>
        ) : null}
      </button>
    </article>
  );
}
